/**
 * Token estimation utility for OpenAI models
 * Based on OpenAI's rough guidelines:
 * - 1 token ~= 4 chars in English
 * - 1 token ~= ¾ words
 * - 100 tokens ~= 75 words
 * 
 * For more accurate counting, consider using the tiktoken library
 * 
 * Current model landscape (as of August 2025):
 * - GPT-4.1 series: Latest flagship models with 1M token context
 *   - GPT-4.1: Standard model for complex tasks
 *   - GPT-4.1 mini: 83% cheaper than GPT-4o, matches performance
 *   - GPT-4.1 nano: Fastest and cheapest for low-latency tasks
 * - O-series: Reasoning models (o3, o4-mini) for deep thinking
 * - GPT-4o: Multimodal model (audio, vision, text)
 * - Deprecated: GPT-4.5 Preview (July 2025), GPT-3.5 (legacy)
 */

/**
 * Estimates token count for a given text
 * This is a rough approximation - for exact counts use tiktoken
 */
export function estimateTokens(text: string): number {
  if (!text) return 0;
  
  // Convert to string if it's an object
  const str = typeof text === 'string' ? text : JSON.stringify(text);
  
  // Rough estimation based on OpenAI's guidelines
  // Using a more nuanced approach than just dividing by 4
  
  // Count words (split by whitespace and punctuation)
  const words = str.split(/[\s\-.,;!?]+/).filter(word => word.length > 0);
  const wordCount = words.length;
  
  // Count characters
  const charCount = str.length;
  
  // Use a weighted average of word-based and character-based estimates
  // Word-based: 1 token per 0.75 words
  const wordBasedEstimate = wordCount / 0.75;
  
  // Character-based: 1 token per 4 characters
  const charBasedEstimate = charCount / 4;
  
  // Weight character-based estimate more heavily for code/JSON
  const hasCodeOrJson = str.includes('{') || str.includes('[') || str.includes('function');
  const weight = hasCodeOrJson ? 0.7 : 0.5;
  
  // Return weighted average, rounded up
  return Math.ceil(charBasedEstimate * weight + wordBasedEstimate * (1 - weight));
}

/**
 * Estimates tokens for common OpenAI models
 * Returns an object with model-specific adjustments
 * 
 * Updated for 2025 models:
 * - GPT-4.1 series (GPT-4.1, GPT-4.1 mini, GPT-4.1 nano)
 * - O-series reasoning models (o3, o4-mini)
 * - GPT-4o (Omni model)
 */
export function estimateTokensForModel(text: string, model: string = 'gpt-4.1'): {
  tokens: number;
  model: string;
  method: 'estimate' | 'exact';
} {
  const baseTokens = estimateTokens(text);
  
  // Different models may have slightly different tokenization
  // These are rough adjustments based on model characteristics
  let adjustment = 1;
  
  if (model.includes('gpt-4.1')) {
    // GPT-4.1 series - improved efficiency over GPT-4o
    if (model.includes('nano')) {
      adjustment = 0.92; // Most efficient model
    } else if (model.includes('mini')) {
      adjustment = 0.95; // 83% cost reduction vs GPT-4o
    } else {
      adjustment = 1.0; // Standard GPT-4.1
    }
  } else if (model.includes('gpt-4o')) {
    adjustment = 1.05; // GPT-4o (Omni) - multimodal overhead
  } else if (model.includes('o3') || model.includes('o4')) {
    // O-series reasoning models - more tokens due to reasoning steps
    adjustment = 1.15; // Reasoning models use more tokens for chain-of-thought
  } else if (model.includes('gpt-4')) {
    adjustment = 1.08; // Legacy GPT-4
  } else if (model.includes('gpt-3.5')) {
    adjustment = 0.98; // Legacy GPT-3.5
  }
  
  return {
    tokens: Math.ceil(baseTokens * adjustment),
    model,
    method: 'estimate'
  };
}

/**
 * Estimates token usage for a request/response pair
 */
export function estimateRequestResponseTokens(
  request: any,
  response: any,
  model: string = 'gpt-4.1-mini'
): {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  model: string;
  method: 'estimate' | 'exact';
} {
  const inputEstimate = estimateTokensForModel(request, model);
  const outputEstimate = estimateTokensForModel(response, model);
  
  return {
    inputTokens: inputEstimate.tokens,
    outputTokens: outputEstimate.tokens,
    totalTokens: inputEstimate.tokens + outputEstimate.tokens,
    model,
    method: 'estimate'
  };
}

/**
 * Format token count for display
 */
export function formatTokenCount(tokens: number): string {
  if (tokens < 1000) {
    return `${tokens} tokens`;
  } else if (tokens < 1000000) {
    return `${(tokens / 1000).toFixed(1)}k tokens`;
  } else {
    return `${(tokens / 1000000).toFixed(2)}M tokens`;
  }
}